import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, ArrowLeft, RefreshCw } from "lucide-react";
import { reportWebError } from "../../lib/api";

interface AdminErrorBoundaryProps {
  children: ReactNode;
  resetKey?: string;
  user?: string | null;
  token?: string | null;
}

interface AdminErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class AdminErrorBoundary extends Component<AdminErrorBoundaryProps, AdminErrorBoundaryState> {
  state: AdminErrorBoundaryState = {
    hasError: false,
    error: null,
    componentStack: null,
  };

  static getDerivedStateFromError(error: Error): Partial<AdminErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const route = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/admin";
    const userIdentifier = this.props.user || "admin";
    const componentStack = info.componentStack || "";

    this.setState({ componentStack });

    console.error("Admin view error caught by AdminErrorBoundary:", error, componentStack);

    // Send error to system/admin event logs
    reportWebError(
      {
        message: `Admin UI Crash: ${error.message || "Unknown error"}`,
        exception_type: error.name || "AdminErrorBoundary",
        source: "admin_web",
        level: "error",
        url: route,
        stack_trace: `${error.stack || ""}\n\nComponent Stack:\n${componentStack}`,
        context: {
          route,
          user: userIdentifier,
          component_stack: componentStack,
        },
      },
      this.props.token,
    );
  }

  componentDidUpdate(prevProps: AdminErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, componentStack: null });
    }
  }

  handleGoBack = () => {
    if (typeof window !== "undefined" && window.history.length > 1) {
      window.history.back();
    } else if (typeof window !== "undefined") {
      window.location.href = "/admin";
    }
  };

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (this.state.hasError) {
      const route = typeof window !== "undefined" ? window.location.pathname : "";
      return (
        <div className="py-12 px-4 max-w-xl mx-auto">
          <div className="bg-card border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-destructive/10 text-destructive border border-destructive/20 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">Section unavailable</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  An error occurred while displaying this admin view.
                </p>
              </div>
            </div>

            <div className="rounded-lg bg-muted/60 border border-border p-3.5 text-xs space-y-1.5 font-mono text-muted-foreground">
              <p className="truncate">
                <strong className="text-foreground">Route:</strong> {route || "/admin"}
              </p>
              {this.state.error && (
                <p className="text-destructive break-words font-medium">
                  <strong>Error:</strong> {this.state.error.message}
                </p>
              )}
            </div>

            <p className="text-xs text-muted-foreground leading-relaxed">
              This incident has been logged to the system audit log. You can navigate back to the previous section or reload this page.
            </p>

            <div className="flex flex-wrap items-center gap-2.5 pt-2 border-t border-border">
              <button
                type="button"
                onClick={this.handleGoBack}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 transition cursor-pointer"
              >
                <ArrowLeft size={14} />
                Go back
              </button>
              <button
                type="button"
                onClick={this.handleTryAgain}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-secondary text-foreground border border-border text-xs font-semibold hover:bg-muted transition cursor-pointer"
              >
                <RefreshCw size={14} />
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
